/* ==========================================================================
   Bhagwati Designer — core JS (vanilla, no dependencies)
   Loaded on every page. Page-specific behaviour lives in:
     cart.js        cart drawer + quantity (every page)
     product.js     variant picker, gallery, sticky add-to-cart (product page)
     collection.js  filters and sorting (collection / search pages)
   Every file registers itself on window.BhagwatiTheme; this file runs the boot
   queue, so it must load first (Shopify keeps <script defer> in document order).
   ========================================================================== */
window.BhagwatiTheme = (function () {
  'use strict';

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  /* ---------------------------------------------------------------------
     Money formatting (uses shop money format from Liquid)
     --------------------------------------------------------------------- */
  var Money = {};
  Money.format = function (cents) {
    var format = window.themeMoneyFormat || '{{amount}}';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var withComma = parts.join('.');
    var withSpace = parts.join(' ');
    var symbol = window.themeCurrencySymbol || '\u20B9';
    if (format.indexOf('amount_no_decimals') !== -1) return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/, parts[0]).replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parts[0]);
    if (format.indexOf('amount_with_apostrophe_separator') !== -1) return format.replace('{{amount_with_apostrophe_separator}}', value.replace(/\B(?=(\d{3})+(?!\d))/g, "'"));
    if (format.indexOf('amount_with_space_separator') !== -1) return format.replace('{{amount_with_space_separator}}', withSpace);
    if (format.indexOf('amount_with_comma_separator') !== -1) return format.replace('{{amount_with_comma_separator}}', withComma);
    return format.replace('{{amount}}', withComma).replace('{{ amount }}', withComma);
  };

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  var Toast = {
    el: null,
    show: function (message, isError) {
      if (!this.el) {
        this.el = document.createElement('div');
        this.el.className = 'toast';
        this.el.setAttribute('role', 'status');
        this.el.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m5 12.8 4.5 4.4L19 6.6"/></svg><span></span>';
        document.body.appendChild(this.el);
      }
      this.el.querySelector('span').textContent = message || 'Done';
      this.el.classList.toggle('toast--error', !!isError);
      this.el.classList.add('is-visible');
      clearTimeout(this._t);
      this._t = setTimeout(function () { Toast.el.classList.remove('is-visible'); }, 3600);
    }
  };
  window.themeToast = Toast;

  /* ---------------------------------------------------------------------
     Predictive search
     --------------------------------------------------------------------- */
  var Search = {
    init: function () {
      var input = $('[data-search-input]');
      var results = $('[data-predictive-results]');
      if (!input || !results) return;
      var timer;
      input.addEventListener('input', function () {
        clearTimeout(timer);
        var term = input.value.trim();
        if (term.length < 2) { results.innerHTML = ''; return; }
        timer = setTimeout(function () {
          fetch('/search/suggest.json?q=' + encodeURIComponent(term) + '&resources[type]=product,collection,article&resources[limit]=6')
            .then(function (r) { return r.json(); })
            .then(function (data) {
              var products = (data.resources && data.resources.results && data.resources.results.products) || [];
              if (!products.length) {
                results.innerHTML = '<div class="predictive__empty">' + window.themeStrings.noResults + '</div>';
                return;
              }
              results.innerHTML = products.map(function (p) {
                return '<a class="predictive__item" href="' + p.url + '">' +
                  (p.featured_image ? '<img src="' + p.featured_image.url + '&width=120" alt="">' : '') +
                  '<div><h4>' + p.title + '</h4><div class="price"><span class="price__current">' + Money.format(p.price) + '</span></div></div></a>';
              }).join('');
            })
            .catch(function () { results.innerHTML = ''; });
        }, 240);
      });
    }
  };

  /* ---------------------------------------------------------------------
     Reveal on scroll + header shadow + accordions
     --------------------------------------------------------------------- */
  var Reveal = {
    init: function () {
      var items = $$('.reveal:not(.is-visible)');
      if (!items.length) return;
      if (!('IntersectionObserver' in window)) {
        items.forEach(function (i) { i.classList.add('is-visible'); });
        return;
      }
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            obs.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      items.forEach(function (i) { io.observe(i); });
    }
  };

  var Header = {
    init: function () {
      var header = $('.header');
      if (header && header.classList.contains('header--sticky')) {
        var onScroll = function () {
          header.classList.toggle('is-scrolled', window.scrollY > 20);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
      $$('[data-accordion-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var item = btn.closest('.accordion__item');
          if (item) item.open = !item.open;
        });
      });
    }
  };

  /* ---------------------------------------------------------------------
     Boot — modules registered by the other files run here
     --------------------------------------------------------------------- */
  var modules = [];
  var theme = {
    $: $,
    $$: $$,
    Money: Money,
    Toast: Toast,
    Reveal: Reveal,
    Header: Header,
    Search: Search,
    modules: modules,
    /* register(name, module, options) — options.rebind: re-run on section load */
    register: function (name, module, options) {
      options = options || {};
      modules.push({ name: name, module: module, rebind: !!options.rebind, booted: false });
    },
    reload: function (rebindOnly) {
      modules.forEach(function (entry) {
        if (entry.booted && !(rebindOnly && entry.rebind)) return;
        if (typeof entry.module.init !== 'function') return;
        // a failure in one module must never stop the others from initialising
        try {
          entry.module.init();
          if (!rebindOnly) entry.booted = true;
        } catch (err) {
          if (window.console && console.error) console.error('[BhagwatiTheme] ' + entry.name + ' failed to init', err);
        }
      });
    }
  };

  // core modules that live in this file
  theme.register('reveal', Reveal);
  theme.register('header', Header);
  theme.register('search', Search);

  document.addEventListener('DOMContentLoaded', function () { theme.reload(); });

  // theme editor: sections re-render without a page load
  document.addEventListener('shopify:section:load', function () { theme.reload(true); });

  return theme;
})();
